import { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, Stack, Alert, IconButton, InputAdornment, Typography, Button, TextField } from '@mui/material';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { enqueueSnackbar } from 'notistack';

import { PATH_AUTH } from '../../../routes/paths';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import useEmbedded from "../../../hooks/useEmbedded";
import useMerchant from 'src/hooks/useMerchant';
import useCarbonUser from 'src/hooks/useCarbonUser';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
import ErrorBanner from 'src/components/banners/ErrorBanner';
import FormHeader from 'src/pages/auth/FormHeader';
import { validationSchema } from '../validation';
import { BASE_URL } from "../../../config";
import { createMerchantCustomerAPI, getCustomerUsersAPI } from 'src/resources/api/customers/index.api';
import MarketingCheckboxes from "../register/MarketingCheckboxes";
import { acceptMerchantInvitationAPI, getMerchantByCodeAPI } from 'src/resources/api/merchant/index.api';
import { IconEye, IconEyeOff } from 'src/libraries/icons';

//
//

type FormValuesProps = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
};

export default function LoginForm() {
  const { login, logout, isAuthenticated, verifyTOTP, resendConfirmationCode } = useAuth();
  const { getAdminRelationshipFromUser, getMerchantUsers, getMerchant } = useMerchant();
  const { getCarbonUser } = useCarbonUser();
  const navigate = useNavigate();

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);
  const { isEmbedded, postMessageToParent } = useEmbedded();
  const [loginErrorMessage, setLoginErrorMessage] = useState<string | JSX.Element>('');
  const [pendingCognitoUser, setPendingCognitoUser] = useState<CognitoUser | null>(null);
  const [pendingEmail, setPendingEmail] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [totpSubmitting, setTotpSubmitting] = useState(false);

  const userNotLinkedToMerchant = <>
    <Typography>
      The login details provided are not currently associated with a CarbonClick business account. If
      you believe this is incorrect or need any further assistance, please don't hesitate to
      reach out to our support team.
    </Typography>
    <Typography sx={{ pt: 1 }}>
      If you aren't a business user, please go to our individuals' portal at <Link style={{ color: 'inherit', fontSize: 'inherit', textDecoration: 'underline' }}  component={Link} href={'https://my.carbon.click'}>https://my.carbon.click</Link> and login there to view your account
    </Typography>
  </>;

  const defaultValues = {
    email: '',
    password: '',
    remember: true,
  };

  const search = new URLSearchParams(useLocation().search);
  const emailParameter = search.get('email');
  const invitedByMerchantCodeParameter = search.get('invitedByMerchantCode');
  const invitedByMerchantNameParameter = search.get('invitedByMerchantName');

  const isInvitationAcceptance = emailParameter && invitedByMerchantCodeParameter;

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isSubmittingAgreement, setIsSubmittingAgreement] = useState(false);
  const [isSendMonthlyNewsletter, setIsSendMonthlyNewsletter] = useState(false);

  const handleTermsAgreementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSubmittingAgreement(event.target.checked);
  };

  const handlePromotionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSendMonthlyNewsletter(event.target.checked);
  };

  const methods = useForm<FormValuesProps>({
    mode: 'onChange',
    resolver: yupResolver(isInvitationAcceptance ? validationSchema.LoginAcceptInvitation : validationSchema.Login),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors },
    watch,
  } = methods;

  const emailWatch = watch('email');
  const passwordWatch = watch('password');

  useEffect(() => {
    if(isAuthenticated && !isSubmittingForm && isInvitationAcceptance) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isSubmittingForm]);

  useEffect(() => {
    setLoginErrorMessage('');
  }, [emailWatch, passwordWatch]);

  const reSendNewVerificationCode = (email: string) => {
    resendConfirmationCode(email)
      .then(() => {
        enqueueSnackbar('Sent new email to ' + email);
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: "error" });
      })
  };

  const handlePostLogin = (email: string) => {
    if (isEmbedded) {
      postMessageToParent({ type: 'PORTAL_LOGGED_IN', payload: BASE_URL });
    }

    if (invitedByMerchantCodeParameter) {
      try {
        const acceptMerchantInvitationData = { email };
        acceptMerchantInvitationAPI(invitedByMerchantCodeParameter, acceptMerchantInvitationData)
            .then(() => {
              return getCustomerUsersAPI(email)
                  .then((user) => {
                    if (isSendMonthlyNewsletter) {
                      const customerUpdateData = {
                        email: email,
                        merchantCode: invitedByMerchantCodeParameter,
                        marketingOptIn: isSendMonthlyNewsletter
                      }
                      createMerchantCustomerAPI(customerUpdateData)
                          .catch((error) => {
                            console.error(error);
                          })
                    }
                    if (user) {
                      const isAdminUser = getAdminRelationshipFromUser(user, user.id);
                      if (isAdminUser) {
                        return getMerchantByCodeAPI({ code: invitedByMerchantCodeParameter })
                            .then((merchant) => {
                              localStorage.setItem('storedMerchantId', JSON.stringify(merchant.id));
                              getMerchant(user, merchant.id);
                            })
                            .catch((error) => {
                              console.error(error);
                            })
                            .finally(() => {
                              getMerchantUsers();
                              // GuestGuard owns the post-auth redirect (destination + subscriptions-
                              // loading gate), but it stands down while the invite params keep
                              // isUserInvited true, and it gates on carbonUser.merchantUsers - which is
                              // stale until we refetch. So refresh the carbon user (now a member) and
                              // drop the invite params; GuestGuard then takes over instead of us
                              // re-implementing its routing here.
                              getCarbonUser(email).finally(() => {
                                navigate({ pathname: PATH_AUTH.login }, { replace: true });
                              });
                            });
                      } else {
                        setIsSubmittingForm(false);
                        setLoginErrorMessage(userNotLinkedToMerchant);
                        logout();
                      }
                    }
                  })
            })
            .catch((error) => {
              console.error(error);
              setIsSubmittingForm(false);
              if (error?.errors?.some((e: any) => e.code === 'INVITATION_ALREADY_ACCEPTED')) {
                setLoginErrorMessage(<>
                  This invitation has already been accepted.
                  To sign in,
                  <Link style={{ fontSize: 'inherit' }} component={RouterLink} to={PATH_AUTH.login}>
                      {' '}click here
                  </Link>
                    .
                </>);
              } else {
                setLoginErrorMessage(error?.errors?.[0]?.message);
              }
            })
      } catch (error) {
        console.error(error);
        setIsSubmittingForm(false);
        setLoginErrorMessage('Something went wrong');
      }
    } else {
      getCustomerUsersAPI(email)
          .then((data) => {
            if (data) {
              const isAdminUser = getAdminRelationshipFromUser(data, data.id);
              if (isAdminUser) {
                // redirect handled by GuestGuard
              } else {
                setLoginErrorMessage(userNotLinkedToMerchant);
                logout();
                setIsSubmittingForm(false);
              }
            }
          })
    }
  };

  const onSubmit = (data: FormValuesProps) => {
    setIsSubmittingForm(true);
    try {
      const email = isInvitationAcceptance ? emailParameter : data.email;

      login(email, data.password)
          .then((result: any) => {
            if (result?.message === 'totpRequired') {
              setPendingCognitoUser(result.cognitoUser);
              setPendingEmail(email);
              setIsSubmittingForm(false);
              return;
            }
            handlePostLogin(email);
          })
          .catch((error) => {
            if (error?.code === 'UserNotConfirmedException') {
              // redirect to verify code page and resend the code
              reSendNewVerificationCode(email);
              navigate({ pathname: PATH_AUTH.verify, search: `?email=${encodeURIComponent(data.email)}` }, { replace: true });
            } else {
              setLoginErrorMessage(error.message);
              setIsSubmittingForm(false);
            }

            logout();
          });
    } catch (error) {
      if (error.code === 'UserNotConfirmedException') {
        setIsSubmittingForm(false);
        navigate({ pathname: PATH_AUTH.verify, search: `?email=${encodeURIComponent(data.email)}` }, { replace: true });
      } else {
        setIsSubmittingForm(false);
        console.error(error);
        reset();
      }
      if (isMountedRef.current) {
        setIsSubmittingForm(false);
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  const onTOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTotpError('');
    setTotpSubmitting(true);
    try {
      await verifyTOTP(pendingCognitoUser!, totpCode);
      handlePostLogin(pendingEmail);
    } catch (error: any) {
      setTotpError(error.message || 'Invalid code. Please try again.');
      setTotpSubmitting(false);
    }
  };

  if (pendingCognitoUser) {
    return (
      <>
        <FormHeader title="Two-factor authentication" />
        <form onSubmit={onTOTPSubmit}>
          <Stack spacing={2}>
            {totpError && <Alert severity="error">{totpError}</Alert>}
            <Alert severity="info">Enter the 6-digit code from your authenticator app.</Alert>
            <TextField
              fullWidth
              label="Authenticator code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
              autoFocus
            />
          </Stack>
          <Button
            type="submit"
            variant="contained"
            loading={totpSubmitting}
            disabled={totpCode.length !== 6}
            fullWidth
            size="small"
            sx={{ mt: 2 }}
          >
            Verify
          </Button>
        </form>
      </>
    );
  }

  const registerFormTitle = isInvitationAcceptance
    ? <>Join the {invitedByMerchantNameParameter} account</>
    : <>Welcome back!</>;

  return (
    <>
    <FormHeader title={registerFormTitle} />

      {isInvitationAcceptance && invitedByMerchantNameParameter && (
        <Typography>
          You’ve been invited to join the {invitedByMerchantNameParameter} account. Please enter your existing CarbonClick password to continue.
        </Typography>
      )}

    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        {loginErrorMessage && (
          <ErrorBanner errorMessage={loginErrorMessage} />
        )}

        {!isInvitationAcceptance && (
          <RHFTextField name="email" size='medium' type="email" label="Email" />
        )}

        <RHFTextField
          name="password"
          label="Password"
          size='medium'
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ mr: 0.5 }}>
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <IconEye size={20} /> : <IconEyeOff size={20} />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={'center'}
        justifyContent="space-between"
        sx={{ mt: 2, mb: 3, mx: 1, '& .MuiTypography-root': { fontSize: 14 } }}
      >
        <RHFCheckbox name="remember" label="Remember me" />
        <Link component={RouterLink} to={PATH_AUTH.resetPassword}>
          Forgot password?
        </Link>
      </Stack>

      {isInvitationAcceptance &&
        <MarketingCheckboxes
          isSubmittingAgreement={isSubmittingAgreement}
          handleTermsAgreementChange={handleTermsAgreementChange}
          handlePromotionChange={handlePromotionChange}
          isSendMonthlyNewsletter={isSendMonthlyNewsletter}
        />
      }

      <Button
        type="submit"
        variant="contained"
        loading={isSubmittingForm}
        fullWidth
        disabled={isInvitationAcceptance ? !isSubmittingAgreement : false}
        size='small'
      >
        {isInvitationAcceptance ? 'Join account' : 'Sign In'}
      </Button>
    </FormProvider>

      {!isInvitationAcceptance && (
        <Typography align="center" variant='body2'>
          Don’t have an account?{' '}
          <Link component={RouterLink} to={PATH_AUTH.register}>
            Get started
          </Link>
        </Typography>
      )}
    </>
  );
}
